/**
 * ExecutionMetrics Utility Functions
 * Isolated helper functions for execution metrics calculations and formatting
 * NO DEPENDENCIES on existing components - fully self-contained
 */

/**
 * Calculate execution quality score (0-100)
 * @param {number} latencyMs - Submission to fill latency in milliseconds
 * @param {number} slippagePips - Slippage in pips (positive = unfavorable)
 * @returns {object} - { score: number, grade: string, color: string }
 */
export const calculateQualityScore = (latencyMs, slippagePips) => {
    // Latency scoring (0-50 points)
    let latencyScore = 0;
    if (latencyMs < 100) latencyScore = 50;
    else if (latencyMs < 200) latencyScore = 45;
    else if (latencyMs < 500) latencyScore = 35;
    else if (latencyMs < 1000) latencyScore = 20;
    else latencyScore = 5;

    // Slippage scoring (0-50 points)
    const absSlippage = Math.abs(slippagePips || 0);
    let slippageScore = 0;
    if (absSlippage < 0.3) slippageScore = 50;
    else if (absSlippage < 0.5) slippageScore = 45;
    else if (absSlippage < 1.0) slippageScore = 35;
    else if (absSlippage < 2.0) slippageScore = 20;
    else slippageScore = 5;

    const totalScore = latencyScore + slippageScore;

    // Grade and color
    let grade, color;
    if (totalScore >= 90) {
        grade = 'Excellent';
        color = '#10b981'; // Green
    } else if (totalScore >= 70) {
        grade = 'Good';
        color = '#f59e0b'; // Yellow/Amber
    } else if (totalScore >= 50) {
        grade = 'Acceptable';
        color = '#f97316'; // Orange
    } else {
        grade = 'Poor';
        color = '#ef4444'; // Red
    }

    return { score: totalScore, grade, color };
};

/**
 * Get performance badge color and icon
 * @param {number} value - The metric value
 * @param {string} metric - Type of metric ('latency' or 'slippage')
 * @returns {object} - { color: string, emoji: string, label: string }
 */
export const getPerformanceBadge = (value, metric) => {
    if (metric === 'latency') {
        if (value < 200) return { color: '#10b981', emoji: '🟢', label: 'Excellent' };
        if (value < 500) return { color: '#f59e0b', emoji: '🟡', label: 'Acceptable' };
        if (value < 1000) return { color: '#f97316', emoji: '🟠', label: 'Slow' };
        return { color: '#ef4444', emoji: '🔴', label: 'Very Slow' };
    }

    if (metric === 'slippage') {
        const absValue = Math.abs(value);
        if (absValue < 0.5) return { color: '#10b981', emoji: '🟢', label: 'Excellent' };
        if (absValue < 1.0) return { color: '#f59e0b', emoji: '🟡', label: 'Acceptable' };
        if (absValue < 2.0) return { color: '#f97316', emoji: '🟠', label: 'Warning' };
        return { color: '#ef4444', emoji: '🔴', label: 'Poor' };
    }

    return { color: '#6b7280', emoji: '⚪', label: 'Unknown' };
};

/**
 * Format timestamp with microseconds
 * @param {string} timestamp - ISO timestamp
 * @returns {string} - Formatted time (HH:MM:SS.ssss)
 */
export const formatTimestamp = (timestamp) => {
    if (!timestamp) return '--:--:--';

    try {
        const date = new Date(timestamp);
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');

        // Extract microseconds from ISO string
        const match = timestamp.match(/\.(\d{3,7})/);
        const microseconds = match ? match[1].substring(0, 4).padEnd(4, '0') : '0000';

        return `${hours}:${minutes}:${seconds}.${microseconds}`;
    } catch (error) {
        console.error('Error formatting timestamp:', error);
        return '--:--:--';
    }
};

/**
 * Format price with proper decimal places
 * @param {number} price - Price value
 * @param {number} decimals - Number of decimal places (default 5)
 * @returns {string} - Formatted price
 */
export const formatPrice = (price, decimals = 5) => {
    if (price === null || price === undefined || isNaN(price)) return '--';
    return parseFloat(price).toFixed(decimals);
};

/**
 * Format slippage with sign and unit
 * @param {number} slippage - Slippage value in pips
 * @returns {string} - Formatted slippage (e.g., "+0.29p" or "-0.12p")
 */
export const formatSlippage = (slippage) => {
    if (slippage === null || slippage === undefined || isNaN(slippage)) return '--';
    const sign = slippage >= 0 ? '+' : '';
    return `${sign}${slippage.toFixed(2)}p`;
};

/**
 * Format latency in milliseconds
 * @param {number} latencyMs - Latency value
 * @returns {string} - Formatted latency (e.g., "250ms")
 */
export const formatLatency = (latencyMs) => {
    if (latencyMs === null || latencyMs === undefined || isNaN(latencyMs)) return '--';
    return `${Math.round(latencyMs)}ms`;
};

/**
 * Get order status display properties
 * @param {string} ordStatus - Order status code
 * @returns {object} - { label: string, color: string, icon: string }
 */
export const getOrderStatusDisplay = (ordStatus) => {
    // FIX OrdStatus codes: 0=New, 1=Partial, 2=Filled, 4=Cancelled, 8=Rejected
    const statusMap = {
        '0': { label: 'New', color: '#3b82f6', icon: '🆕' },
        '1': { label: 'Partial', color: '#f59e0b', icon: '⚠️' },
        '2': { label: 'Filled', color: '#10b981', icon: '✅' },
        '4': { label: 'Cancelled', color: '#6b7280', icon: '❌' },
        '8': { label: 'Rejected', color: '#ef4444', icon: '🚫' },
    };

    return statusMap[String(ordStatus)] || { label: 'Unknown', color: '#6b7280', icon: '❓' };
};

/**
 * Get side display properties
 * @param {string} side - Order side ('Buy' or 'Sell')
 * @returns {object} - { label: string, color: string, icon: string, bgColor: string }
 */
export const getSideDisplay = (side) => {
    if (side === 'Buy' || side === 'buy') {
        return {
            label: 'BUY',
            color: '#ffffff',
            icon: '🔺',
            bgColor: '#10b981'
        };
    } else if (side === 'Sell' || side === 'sell') {
        return {
            label: 'SELL',
            color: '#ffffff',
            icon: '🔻',
            bgColor: '#ef4444'
        };
    }
    return {
        label: side || 'N/A',
        color: '#6b7280',
        icon: '❓',
        bgColor: '#e5e7eb'
    };
};

/**
 * Calculate average metrics from execution list
 * @param {Array} executions - Array of execution objects
 * @returns {object} - { avgLatency, avgSlippage, totalCount, activeCount }
 */
export const calculateAverageMetrics = (executions) => {
    if (!executions || executions.length === 0) {
        return {
            avgLatency: 0,
            avgSlippage: 0,
            totalCount: 0,
            activeCount: 0,
            bestLatency: 0,
            worstLatency: 0
        };
    }

    const validExecutions = executions.filter(ex =>
        (ex.totalLatencyMs !== null && ex.totalLatencyMs !== undefined) ||
        (ex.submissionToFillLatencyMs !== null && ex.submissionToFillLatencyMs !== undefined)
    );

    // Use totalLatencyMs (server-measured) with defensive fallback to submissionToFillLatencyMs
    const latencies = validExecutions.map(ex => ex.totalLatencyMs || ex.submissionToFillLatencyMs);
    const slippages = validExecutions.map(ex => ex.slippagePips || 0);

    const avgLatency = latencies.length > 0
        ? latencies.reduce((sum, val) => sum + val, 0) / latencies.length
        : 0;

    const avgSlippage = slippages.length > 0
        ? slippages.reduce((sum, val) => sum + val, 0) / slippages.length
        : 0;

    const bestLatency = latencies.length > 0 ? Math.min(...latencies) : 0;
    const worstLatency = latencies.length > 0 ? Math.max(...latencies) : 0;

    // Count executions in last 5 seconds as "active"
    const now = Date.now();
    const activeCount = executions.filter(ex => {
        if (!ex.time) return false;
        const exTime = new Date(ex.time).getTime();
        return (now - exTime) < 5000; // 5 seconds
    }).length;

    return {
        avgLatency: Math.round(avgLatency),
        avgSlippage: parseFloat(avgSlippage.toFixed(2)),
        totalCount: executions.length,
        activeCount,
        bestLatency: Math.round(bestLatency),
        worstLatency: Math.round(worstLatency)
    };
};

/**
 * Check if execution is new (less than 5 seconds old)
 * @param {string} timestamp - Execution timestamp
 * @returns {boolean}
 */
export const isNewExecution = (timestamp) => {
    if (!timestamp) return false;
    try {
        const exTime = new Date(timestamp).getTime();
        const now = Date.now();
        return (now - exTime) < 5000; // 5 seconds
    } catch {
        return false;
    }
};

/**
 * Truncate long string with ellipsis
 * @param {string} str - String to truncate
 * @param {number} maxLength - Maximum length
 * @returns {string}
 */
export const truncateString = (str, maxLength = 20) => {
    if (!str) return '';
    if (str.length <= maxLength) return str;
    return `${str.substring(0, maxLength)}...`;
};

/**
 * Get intent display label
 * @param {string} intent - Intent code (O=Open, C=Close, M=Modify)
 * @returns {string}
 */
export const getIntentLabel = (intent) => {
    const intentMap = {
        'O': 'Open',
        'C': 'Close',
        'M': 'Modify'
    };
    return intentMap[intent] || intent || 'Unknown';
};
